import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { ICampaign } from '@/api/routes/campaigns/types';
import type { ResponseWithPagination } from '@/utils/types';

import { fetchCampaigns } from './actionCreators';

interface InitialState {
  campaigns: ICampaign [],
  prevCampaigns: ICampaign [],
  offset: number,
  totalCount: number,
  prevTotalCount: number,
  isLoading: boolean,
  error: string,
}

const initialState: InitialState = {
  campaigns: [],
  prevCampaigns: [],
  offset: 0,
  totalCount: 0,
  prevTotalCount: 0,
  isLoading: false,
  error: '',
};

export const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    toggleCampaignStatus: (state, action: PayloadAction<string>) => {
      const index = state.campaigns.findIndex((el) => el.id === action.payload);
      state.campaigns[index].enabled = !state.campaigns[index].enabled;
    },
    toggleCampaignOrgStatus: (state, action: PayloadAction<string>) => {
      const index = state.campaigns.findIndex((el) => el.id === action.payload);
      state.campaigns[index].org_enabled = !state.campaigns[index].org_enabled;
    },
    toggleAdStatus: (state, action: PayloadAction<{ campaignId: string, id: string }>) => {
      const campIndex = state.campaigns.findIndex((el) => el.id === action.payload.campaignId);
      const adIndex = state.campaigns[campIndex].ads.findIndex((el) => el.id === action.payload.id);
      state.campaigns[campIndex].ads[adIndex].enabled = !state.campaigns[campIndex].ads[adIndex].enabled;
    },
    toggleAdOrgStatus: (state, action: PayloadAction<{ campaignId: string, id: string }>) => {
      const campIndex = state.campaigns.findIndex((el) => el.id === action.payload.campaignId);
      const adIndex = state.campaigns[campIndex].ads.findIndex((el) => el.id === action.payload.id);
      state.campaigns[campIndex].ads[adIndex].org_enabled = !state.campaigns[campIndex].ads[adIndex].org_enabled;
    },
    toggleAdminModerationAd: (state, action: PayloadAction<{
      campaignId: string, id: string, adminModState: number
    }>) => {
      const campIndex = state.campaigns.findIndex((el) => el.id === action.payload.campaignId);
      const adIndex = state.campaigns[campIndex].ads.findIndex((el) => el.id === action.payload.id);
      state.campaigns[campIndex].ads[adIndex].admin_mod_state = action.payload.adminModState;
    },
    retryModerationAd: (state, action: PayloadAction<{ campaignId: string, id: string }>) => {
      const campIndex = state.campaigns.findIndex((el) => el.id === action.payload.campaignId);
      const adIndex = state.campaigns[campIndex].ads.findIndex((el) => el.id === action.payload.id);
      state.campaigns[campIndex].ads[adIndex].mod_state = -1;
    },
    forceModerationAd: (state, action: PayloadAction<{ campaignId: string, id: string }>) => {
      const campIndex = state.campaigns.findIndex((el) => el.id === action.payload.campaignId);
      const adIndex = state.campaigns[campIndex].ads.findIndex((el) => el.id === action.payload.id);
      state.campaigns[campIndex].ads[adIndex].mod_force_yes = !state.campaigns[campIndex].ads[adIndex].mod_force_yes;
    },
  },
  extraReducers: {
    [fetchCampaigns.fulfilled.type]: (state, action: PayloadAction<ResponseWithPagination<ICampaign>>) => {
      state.isLoading = false;
      state.error = '';
      state.prevCampaigns = state.campaigns;
      state.campaigns = action.payload.rows || [];
      state.offset = action.payload.offset;
      state.prevTotalCount = state.totalCount;
      state.totalCount = action.payload.total_count;
    },
    [fetchCampaigns.pending.type]: (state) => {
      state.isLoading = true;
    },
    [fetchCampaigns.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

export const {
  toggleCampaignStatus,
  toggleCampaignOrgStatus,
  toggleAdStatus,
  toggleAdOrgStatus,
  toggleAdminModerationAd,
  retryModerationAd,
  forceModerationAd,
} = campaignsSlice.actions;
export default campaignsSlice.reducer;
